import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Store } from 'types/store';
import { VehicleType } from 'types/common/vehicles';
import { RLRProductType, RLRVehicleInfoEnum } from 'constants/journeys/loyaltyRewardsEnums';
import { setChosenVehicle } from 'actions/journeys/loyaltyRewardsActions';
import smallTick from 'assets/images/icons/small-tick.svg';
import SearchCard from './SearchCard';
import styles from './RegistrationSelect.module.scss';

interface RegistrationSelectProps {
  product: RLRProductType;
}

const RegistrationSelect: React.FC<RegistrationSelectProps> = ({ product }) => {
  const dispatch = useDispatch();
  const vehicles = useSelector((state: Store) => state.journeys.loyaltyRewards.vehicles);
  const vehicleDisplayModality = useSelector((state: Store) => state.journeys.loyaltyRewards.vehicleDisplayModality);
  const chosenVehicle = useSelector((state: Store) => state.journeys.loyaltyRewards.data.chosenVehicle);

  const [vehicleDetailsModalOpen, setVehicleDetailsModalOpen] = useState<boolean>(false);
  const [cardOpen, setCardOpen] = useState<boolean>(false);

  const hasPolicyVehicle = vehicleDisplayModality === RLRVehicleInfoEnum.HasVerifiedExistingPolicyVehicle;
  const vehicleLimitReached = vehicles?.length >= 9;

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCardOpen(true);
    }, 100);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (cardOpen && vehicles?.length === 1 && !chosenVehicle) {
      dispatch(setChosenVehicle(vehicles[0]));
    }
  }, [cardOpen, vehicles, chosenVehicle, dispatch]);

  const handleVehicleSelect = (vehicle: VehicleType) => {
    const isSelected = vehicle.registration === chosenVehicle?.registration;

    if (hasPolicyVehicle && vehicles.length === 1) return;

    dispatch(setChosenVehicle(isSelected ? null : vehicle));
  };

  return (
    <div
      className={`${styles.vehicleCoverCard} ${cardOpen ? styles.vehicleCoverCardOpen : ''}`}
      role="presentation"
      onClick={(event) => event.stopPropagation()}
      onKeyUp={(event) => event.stopPropagation()}
    >
      {hasPolicyVehicle ? (
        <span className={styles.vehicleCardParagraph}>
          {product.title} can only be added to your <b>policy vehicle</b>.
        </span>
      ) : (
        <>
          <span className={styles.vehicleCoverCardTitle}>
            Which vehicle would you like to cover?
          </span><br />
          <span className={styles.vehicleCardParagraph}>
            {product.title} can only be applied to <b>one</b> nominated vehicle.
          </span>
        </>
      )}

      {!hasPolicyVehicle && (
        <div className={!vehicles?.length ? 'pb-4' : ''}>
          <SearchCard
            isVehicleModalOpen={vehicleDetailsModalOpen}
            toggleVehicleModal={setVehicleDetailsModalOpen}
          />
        </div>
      )}

      {vehicles?.length > 0 && (
        <div className={styles.registrationContainer}>
          {vehicles.map((vehicle) => {
            const isSelected = vehicle.registration === chosenVehicle?.registration;
            const buttonSelectedClass = isSelected ? styles.selectedRegistration : styles.grayedOutRegistration;
            const buttonClass = chosenVehicle?.registration ? buttonSelectedClass : styles.registration;

            return (
              <button
                key={vehicle.registration}
                type="button"
                className={buttonClass}
                onClick={() => handleVehicleSelect(vehicle)}
              >
                {vehicle.registration}
                <img
                  src={smallTick}
                  alt="Selected registration"
                  style={{
                    maxWidth: isSelected ? '12px' : 0,
                    marginLeft: isSelected ? '10px' : 0,
                  }}
                />
              </button>
            );
          })}
        </div>
      )}

      {!hasPolicyVehicle && !vehicleLimitReached && vehicles?.length > 0 && (
        <button
          type="button"
          className={styles.addVehicle}
          onClick={() => setVehicleDetailsModalOpen(true)}
        >
          <u>
            Cover a different vehicle
          </u>
        </button>
      )}
    </div>
  );
};

export default RegistrationSelect;
